import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import { useTheme } from '@material-ui/core/styles';
import { colors } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import paletteTypes from 'common/paletteTypes';

const Topbar = ({
  themeMode,
  themeToggler,
  setThemePalette,
  paletteType,
}) => {
  const theme = useTheme();
  const getPaletteSwatch = (item) => (item === 'olive' ? '#2B3A1D' : (colors[item]?.[700] ?? theme.palette.primary.main));
  const { pathname } = useLocation();
  const pathNoTrail = (pathname || '').replace(/\/+$/, '') || '/';
  const isExpedientesPage = pathname === '/expedientesdeldelito' || (typeof window !== 'undefined' && window.location.hostname === 'expedientesdeldelito.tnonetwork.com');
  const showMediaKitNav =
    pathNoTrail === '/' || pathNoTrail === '/programacion' || pathNoTrail === '/paquetes' || pathNoTrail === '/media-kits';
  const logoSrc =
    themeMode === 'dark' ? '/images/logos/logo_white.PNG' : '/images/logos/logo_tnonetwork_black.png';

  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      width={'100%'}
    >
      {showMediaKitNav ? (
        <Box display="flex" alignItems="center" sx={{ gap: { xs: 1.5, sm: 2.5 } }}>
          <Link
            component={RouterLink}
            to="/"
            underline="none"
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
            aria-label="TNO Network — media kit"
          >
            <Box
              component="img"
              src={logoSrc}
              alt="TNO Network"
              sx={{ height: { xs: 32, sm: 38 }, width: 'auto', display: 'block' }}
            />
          </Link>
          <Link
            component={RouterLink}
            to="/acerca"
            underline="hover"
            color="textPrimary"
            sx={{ fontWeight: 600, fontSize: '0.9rem', display: { xs: 'none', sm: 'inline' } }}
          >
            Acerca de
          </Link>
          <Link
            component={RouterLink}
            to="/programacion"
            underline="hover"
            color={pathNoTrail === '/programacion' ? 'primary' : 'textSecondary'}
            sx={{
              fontWeight: pathNoTrail === '/programacion' ? 700 : 500,
              fontSize: '0.9rem',
              display: { xs: 'none', md: 'inline' },
            }}
          >
            Programación
          </Link>
          <Link
            component={RouterLink}
            to="/paquetes"
            underline="hover"
            color={pathNoTrail === '/paquetes' ? 'primary' : 'textSecondary'}
            sx={{
              fontWeight: pathNoTrail === '/paquetes' ? 700 : 500,
              fontSize: '0.9rem',
              display: { xs: 'none', md: 'inline' },
            }}
          >
            Paquetes
          </Link>
          <Link
            component={RouterLink}
            to="/media-kits"
            underline="hover"
            color={pathNoTrail === '/media-kits' ? 'primary' : 'textSecondary'}
            sx={{
              fontWeight: pathNoTrail === '/media-kits' ? 700 : 500,
              fontSize: '0.9rem',
              display: { xs: 'none', md: 'inline' },
            }}
          >
            Media Kits
          </Link>
        </Box>
      ) : (
        <Box />
      )}
      <Box display="flex" alignItems={'center'}>
        {!isExpedientesPage && (
          <Box
            display={'flex'}
            padding={1}
            borderRadius={8}
            bgcolor={theme.palette.alternate.main}
          >
            {paletteTypes.map((item, i) => (
              <Box
                key={item}
                bgcolor={getPaletteSwatch(item)}
                width={20}
                height={20}
                borderRadius={'100%'}
                marginRight={i === paletteTypes.length - 1 ? 0 : 1}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ cursor: 'pointer' }}
                onClick={() => setThemePalette(item)}
              >
                {paletteType === item && (
                  <svg
                    width={12}
                    height={12}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </Box>
            ))}
          </Box>
        )}
        {!isExpedientesPage && (
          <Box>
            <IconButton
              onClick={() => themeToggler()}
              aria-label="Dark mode toggler"
              color={themeMode === 'light' ? 'primary' : 'secondary'}
            >
              {themeMode === 'light' ? (
                <svg
                  width={24}
                  height={24}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              ) : (
                <svg
                  width={24}
                  height={24}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

Topbar.propTypes = {
  onSidebarOpen: PropTypes.func,
  themeToggler: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
  setThemePalette: PropTypes.func.isRequired,
  paletteType: PropTypes.string.isRequired,
};

export default Topbar;
